import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/enterprise")({
  head: () => ({
    meta: [
      { title: "Enterprise Inquiry | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Get in touch with the Recovery Trauma™ team about Enterprise plans for organizations, clinics, and trauma-informed teams.",
      },
      { property: "og:title", content: "Enterprise Inquiry | Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Reach out about Enterprise plans for your organization. We'll get back to you within 1–2 business days.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/enterprise" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/enterprise" }],
  }),
  component: EnterprisePage,
});

const schema = z.object({
  name: z.string().trim().min(1, "Please share your name").max(100),
  company: z.string().trim().min(1, "Please share your organization").max(150),
  email: z.string().trim().email("Please enter a valid email").max(255),
  notes: z.string().trim().max(2000).optional(),
});

function EnterprisePage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check your details");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("enterprise_inquiries").insert({
      name: parsed.data.name,
      company: parsed.data.company,
      email: parsed.data.email,
      notes: parsed.data.notes || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    setDone(true);
    toast.success("Thank you — we'll be in touch soon.");
  };

  return (
    <div className="min-h-dvh bg-background py-12 px-4">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
            <Building2 className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Talk to us about Enterprise</h1>
          <p className="mt-3 text-muted-foreground">
            For organizations, clinics, and trauma-informed teams. Share a few details and our team
            will reach out within 1–2 business days.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Enterprise inquiry</CardTitle>
            <CardDescription>All fields marked * are required.</CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="flex flex-col items-center text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-primary mb-3" />
                <h2 className="text-xl font-semibold">Thank you, {form.name.split(" ")[0]}.</h2>
                <p className="mt-2 text-muted-foreground">
                  We've received your inquiry and will be in touch at{" "}
                  <span className="font-medium text-foreground">{form.email}</span> shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Organization *</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    maxLength={150}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Work email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    maxLength={255}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    maxLength={2000}
                    rows={5}
                    placeholder="Team size, use case, timeline, questions…"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full min-h-[48px] touch-manipulation sm:min-h-0" disabled={submitting}>
                  {submitting ? "Sending…" : "Send inquiry"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
